import {
  EditMealPlanData,
  mealPlanMolecule,
} from '@web-app/atoms/charting/mealPlanMolecule';
import {
  FullDetailedMealPlanWithKeys,
  MealPlanInstructionsEntityResponse,
  MealPlanSummaryItem,
} from '@web-app/server/api/types';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';

import {
  MealPlanAnalyticsContext,
  MealPlanView,
} from '../components/CreateMealPlan/utils';
import {
  useMealPlanGeneration,
  UseMealPlanGenerationReturn,
} from './useMealPlanGeneration';
import {
  useMealPlanMutations,
  UseMealPlanMutationsReturn,
} from './useMealPlanMutations';

// ============================================================================
// Types
// ============================================================================

interface UseMealPlanWizardOptions {
  onMealPlanSent?: () => void;
  usePreviousAppointment?: boolean;
}

export interface UseMealPlanWizardReturn
  extends
    UseMealPlanGenerationReturn,
    Omit<UseMealPlanMutationsReturn, 'isMealPlanBusy' | 'isMealPlanUpdating'> {
  // Data
  mealPlanInstructions: MealPlanInstructionsEntityResponse | null;
  mealPlanSummary: MealPlanSummaryItem | null;
  detailedMealPlan: FullDetailedMealPlanWithKeys | null;

  // Data setters
  setMealPlanInstructions: (
    data: MealPlanInstructionsEntityResponse | null | undefined,
  ) => void;
  setMealPlanSummary: (data: MealPlanSummaryItem | null) => void;
  setDetailedMealPlan: (
    data: FullDetailedMealPlanWithKeys | null | undefined,
  ) => void;

  // Step navigation
  activeMealPlanStep: MealPlanView;
  setActiveMealPlanStep: (step: MealPlanView) => void;
  hasSetInitialStep: boolean;
  setHasSetInitialStep: (value: boolean) => void;

  // Edit state
  editDetailedMealPlan: EditMealPlanData | null;
  setEditDetailedMealPlan: (data: EditMealPlanData | null) => void;
  detailedMealPlanEditMode: boolean;
  setDetailedMealPlanEditMode: (mode: boolean) => void;

  // Loading
  areMealPlanInstructionsLoading: boolean;
  isMealPlanSummaryLoading: boolean;
  isDetailedMealPlanLoading: boolean;

  // Reset
  resetMealPlanState: () => void;

  // Wizard orchestration
  performMealPlanAction: () => Promise<boolean>;

  // Macros
  includeMacroDataInMealPlan: boolean;
  setIncludeMacroDataInMealPlan: (include: boolean) => void;

  // Combined loading states
  isMealPlanBusy: boolean;
  isMealPlanUpdating: boolean;
  isAnyMealPlanLoading: boolean;

  // Analytics
  analyticsContext: MealPlanAnalyticsContext;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Main hook for the meal plan wizard flow.
 *
 * Combines useMealPlan (data) and useMealPlanMutations (mutations) with
 * wizard orchestration logic. Use this hook in components that need the
 * full meal plan creation workflow.
 *
 * @example
 * ```tsx
 * const {
 *   activeMealPlanStep,
 *   performMealPlanAction,
 *   isMealPlanBusy,
 *   mealPlanInstructions,
 *   mealPlanSummary,
 *   detailedMealPlan,
 * } = useMealPlanWizard({ onMealPlanSent: () => router.push('/tasks') });
 *
 * // Button click advances through wizard:
 * // Instructions → Summary → Details → Send
 * <Button onClick={performMealPlanAction} isLoading={isMealPlanBusy}>
 *   {getButtonLabel(activeMealPlanStep)}
 * </Button>
 * ```
 */
export function useMealPlanWizard(
  options?: UseMealPlanWizardOptions,
): UseMealPlanWizardReturn {
  // Data layer — access molecule directly for selective subscriptions
  const {
    activeMealPlanStepAtom,
    hasSetInitialStepAtom,
    areMealPlanInstructionsLoadingAtom,
    isMealPlanSummaryLoadingAtom,
    isDetailedMealPlanLoadingAtom,
    mealPlanInstructionsAtom,
    mealPlanSummaryAtom,
    detailedMealPlanAtom,
    editDetailedMealPlanAtom,
    detailedMealPlanEditModeAtom,
    includeMacroDataAtom,
    resetMealPlanAtom,
    analyticsContextAtom,
  } = useMolecule(mealPlanMolecule);

  const [mealPlanInstructions, setMealPlanInstructions] = useAtom(
    mealPlanInstructionsAtom,
  );
  const [mealPlanSummary, setMealPlanSummary] = useAtom(mealPlanSummaryAtom);
  const [detailedMealPlan, setDetailedMealPlan] = useAtom(detailedMealPlanAtom);
  const [activeMealPlanStep, setActiveMealPlanStep] = useAtom(
    activeMealPlanStepAtom,
  );
  const [hasSetInitialStep, setHasSetInitialStep] = useAtom(
    hasSetInitialStepAtom,
  );
  const [editDetailedMealPlan, setEditDetailedMealPlan] = useAtom(
    editDetailedMealPlanAtom,
  );
  const [detailedMealPlanEditMode, setDetailedMealPlanEditMode] = useAtom(
    detailedMealPlanEditModeAtom,
  );
  const [includeMacroDataInMealPlan, setIncludeMacroDataInMealPlan] =
    useAtom(includeMacroDataAtom);
  const areMealPlanInstructionsLoading = useAtomValue(
    areMealPlanInstructionsLoadingAtom,
  );
  const isMealPlanSummaryLoading = useAtomValue(isMealPlanSummaryLoadingAtom);
  const isDetailedMealPlanLoading = useAtomValue(isDetailedMealPlanLoadingAtom);
  const analyticsContext = useAtomValue(analyticsContextAtom);
  const resetMealPlanState = useSetAtom(resetMealPlanAtom);

  // Generation layer (instructions, summary, detailed plan)
  const mealPlanGeneration = useMealPlanGeneration({
    usePreviousAppointment: options?.usePreviousAppointment,
  });

  // Mutations layer (edit, send)
  const mealPlanMutations = useMealPlanMutations();

  // ============================================================================
  // Derived loading state
  // ============================================================================

  const isAnyMealPlanLoading =
    areMealPlanInstructionsLoading ||
    isMealPlanSummaryLoading ||
    isDetailedMealPlanLoading ||
    mealPlanGeneration.isGeneratingInstructions ||
    mealPlanGeneration.isGeneratingSummary ||
    mealPlanGeneration.isGeneratingDetailedPlan ||
    mealPlanMutations.isMealPlanBusy ||
    mealPlanMutations.isSendingMealPlan;

  // ============================================================================
  // Wizard orchestration
  // ============================================================================

  // Destructure functions and state for stable useCallback deps
  const { generateSummary, generateDetailedPlan } = mealPlanGeneration;
  const { sendMealPlan, isMealPlanBusy } = mealPlanMutations;

  // Use a ref so performMealPlanAction doesn't need to re-create when the
  // caller passes an inline callback (e.g. () => router.push(...)).
  const onMealPlanSentRef = useRef(options?.onMealPlanSent);
  onMealPlanSentRef.current = options?.onMealPlanSent;

  /**
   * Main wizard action that advances through the meal plan creation flow.
   * Returns true if the action succeeded, false otherwise.
   *
   * - Instructions step: Generates summary, advances to Summary
   * - Summary step: Generates detailed plan, advances to Details
   * - Details step: Saves any edits, sends the meal plan
   */
  const performMealPlanAction = useCallback(async (): Promise<boolean> => {
    // Belt-and-suspenders guard: UI disables the button via isMealPlanBusy, but
    // there's a window between click and the async setBusy(true) where a second
    // click can slip through. Bail early to prevent concurrent invocations.
    if (isMealPlanBusy) return false;

    switch (activeMealPlanStep) {
      case MealPlanView.Instructions: {
        const success = await generateSummary();
        if (success) {
          setActiveMealPlanStep(MealPlanView.Summary);
        }
        return success;
      }

      case MealPlanView.Summary: {
        const success = await generateDetailedPlan();
        if (success) {
          setActiveMealPlanStep(MealPlanView.Details);
        }
        return success;
      }

      case MealPlanView.Details: {
        const success = await sendMealPlan(includeMacroDataInMealPlan);
        if (success) {
          onMealPlanSentRef.current?.();
        }
        return success;
      }

      default:
        return false;
    }
  }, [
    activeMealPlanStep,
    generateDetailedPlan,
    generateSummary,
    includeMacroDataInMealPlan,
    isMealPlanBusy,
    sendMealPlan,
    setActiveMealPlanStep,
  ]);

  return {
    // Data
    mealPlanInstructions,
    mealPlanSummary,
    detailedMealPlan,

    // Setters
    setMealPlanInstructions,
    setMealPlanSummary,
    setDetailedMealPlan,

    // Edit state
    editDetailedMealPlan,
    setEditDetailedMealPlan,
    detailedMealPlanEditMode,
    setDetailedMealPlanEditMode,

    // Query loading states
    areMealPlanInstructionsLoading,
    isMealPlanSummaryLoading,
    isDetailedMealPlanLoading,

    // Reset
    resetMealPlanState,

    // Generation
    generateInstructions: mealPlanGeneration.generateInstructions,
    generateSummary: mealPlanGeneration.generateSummary,
    generateDetailedPlan: mealPlanGeneration.generateDetailedPlan,

    // Generation loading states
    isGeneratingInstructions: mealPlanGeneration.isGeneratingInstructions,
    isGeneratingSummary: mealPlanGeneration.isGeneratingSummary,
    isGeneratingDetailedPlan: mealPlanGeneration.isGeneratingDetailedPlan,

    // Mutations
    sendMealPlan: mealPlanMutations.sendMealPlan,
    saveDetailedMealPlanEdit: mealPlanMutations.saveDetailedMealPlanEdit,

    // Mutation loading states
    isSendingMealPlan: mealPlanMutations.isSendingMealPlan,

    // Wizard orchestration
    performMealPlanAction,

    // Step navigation
    activeMealPlanStep,
    setActiveMealPlanStep,
    hasSetInitialStep,
    setHasSetInitialStep,

    // Macro toggle
    includeMacroDataInMealPlan,
    setIncludeMacroDataInMealPlan,

    // Combined loading states
    isMealPlanBusy: mealPlanMutations.isMealPlanBusy,
    isMealPlanUpdating: mealPlanMutations.isMealPlanUpdating,
    isAnyMealPlanLoading,

    // Analytics
    analyticsContext,
  };
}
